import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getLocale, getString } from '../../../locale'
import Loader from '../../../components/loader'
import {
    getTestTubeVolume,
    setTestTubeVolume,
    getRinseDuration,
    setRinseDuration,
    getCupVolume,
    setCupVolume,
    getBottleVolume,
    setBottleVolume,
    startCupVolumeMeasure,
    startBottleVolumeMeasure,
    stopVolumeMeasure,
    getCalibrationStopConfirm,
    setCalibrationStopConfirm,
    getLedCount,
    setLedCount,
    getManualPourMode,
    setManualPourMode,
    getHomeLogo,
    setHomeLogo,
    getPouringCompletionWindow,
    setPouringCompletionWindow,
    getInactivityTimeout,
    setInactivityTimeout,
    getAutoRinseConfig,
    setAutoRinseConfig,
    getHomeScreensaver,
    setHomeScreensaver,
    getRemoteLiveLogStream,
    setRemoteLiveLogStream,
    getRemoteTransportMode,
    setRemoteTransportMode,
    getSetupCompleted,
    setSetupCompleted,
    getDailyAutoRestart,
    setDailyAutoRestart,
    getHttpPollingInterval,
    setHttpPollingInterval,
    getLoggingEnabled,
    setLoggingEnabled,
    getSanitationPolicy,
    setSanitationPolicy,
    getOperationMode,
    setOperationMode,
} from '../../../api/0x0005/general-settings'
import { CatalogLocalImage, getCatalogLocalList } from '../../../api/0x0005/catalog'
import { getDrinkList, DrinkInfo } from '../../../api/0x0005/drinks'
import { onVolumeMeasureStatus, IVolumeMeasureStatus } from '../../../wsclient/handlers/volume-measure-status.handler'

type Props = {
    clientId: string
    onRemoteLiveLogStreamChanged?: (enabled: boolean) => void
}

const ADDITIONAL_POURING_TIME_OPTIONS: number[] = [0, 500, 1000, 1500, 2000]

function normalizeAdditionalPouringTime(value?: number): number {
    if (value !== undefined && ADDITIONAL_POURING_TIME_OPTIONS.includes(value)) return value
    return 1000
}

function formatAdditionalPouringTime(value?: number): string {
    const normalized = normalizeAdditionalPouringTime(value)
    const isHebrew = getLocale() === 'he'
    const unit = isHebrew ? "שנ׳" : 's'
    switch (normalized) {
        case 0:
            return '0'
        case 500:
            return `0.5 ${unit}`
        case 1000:
            return `1 ${unit}`
        case 1500:
            return `1.5 ${unit}`
        case 2000:
            return `2 ${unit}`
        default:
            return `1 ${unit}`
    }
}

type EditableSetting = 'testTube' | 'rinse' | 'cup' | 'bottle' | 'calibrationStopConfirm' | 'ledCount' | 'manualHold' | 'homeLogo' | 'pouringCompletionWindow' | 'inactivityTimeout' | 'autoRinseDuration' | 'homeScreensaverEnabled' | 'homeScreensaverTimeout' | 'remoteLiveLogStream' | 'remoteTransportMode' | 'setupCompleted' | 'dailyAutoRestart' | 'httpPollingInterval' | 'loggingEnabled' | 'sanitationEnabled' | 'operationMode'

type State = {
    loading: boolean
    testTubeVolume?: number
    rinseDuration?: number
    cupVolume?: number
    bottleVolume?: number
    calibrationStopConfirmMs?: number
    ledCount?: number
    manualHoldEnabled?: number
    homeLogo?: string
    pouringCompletionWindowMs?: number
    inactivityTimeoutSec?: number
    autoRinseDurationSec?: number
    homeScreensaverEnabled?: number
    homeScreensaverTimeoutMin?: number
    remoteLiveLogStreamEnabled?: number
    remoteTransportMode?: number
    setupCompleted?: number
    dailyAutoRestartEnabled?: number
    httpPollingIntervalMs?: number
    loggingEnabled?: number
    sanitationEnabled?: number
    operationMode?: string
    homeLogoOptions: CatalogLocalImage[]
    showEditModal: boolean
    editingSetting?: EditableSetting
    editValue: string
    showMeasureModal: boolean
    measuringSetting?: 'cup' | 'bottle'
    drinks: DrinkInfo[]
    loadingDrinks: boolean
    showPouringModal: boolean
    pouringDrinkName?: string
    showSaveModal: boolean
    measuredVolume?: number
    liveMeasuredVolume?: number
    liveIsPouring?: number
    liveMeasureError?: string
}

enum SettingType {
    TestTube = 'testTube',
    Rinse = 'rinse',
    Cup = 'cup',
    Bottle = 'bottle',
    CalibrationStopConfirm = 'calibrationStopConfirm',
    LedCount = 'ledCount',
    ManualHold = 'manualHold',
    HomeLogo = 'homeLogo',
    PouringCompletionWindow = 'pouringCompletionWindow',
    InactivityTimeout = 'inactivityTimeout',
    AutoRinseDuration = 'autoRinseDuration',
    HomeScreensaverEnabled = 'homeScreensaverEnabled',
    HomeScreensaverTimeout = 'homeScreensaverTimeout',
    RemoteLiveLogStream = 'remoteLiveLogStream',
    RemoteTransportMode = 'remoteTransportMode',
    SetupCompleted = 'setupCompleted',
    DailyAutoRestart = 'dailyAutoRestart',
    HttpPollingInterval = 'httpPollingInterval',
    LoggingEnabled = 'loggingEnabled',
    SanitationEnabled = 'sanitationEnabled',
    OperationMode = 'operationMode',
}

type SettingInfo = {
    title: string
    desc: string
    value?: number | string
    unit?: string
    min?: number
    max?: number
    rangeMsg?: string
    inputType: 'number' | 'string' | 'boolean' | 'select'
    canMeasure?: boolean
}

export default class GeneralSettingsTab extends Component<Props, State> {
    private unsubscribeVolumeMeasureStatus?: () => void

    state: State = {
        loading: true,
        showEditModal: false,
        editValue: '',
        showMeasureModal: false,
        drinks: [],
        loadingDrinks: false,
        showPouringModal: false,
        showSaveModal: false,
        homeLogoOptions: [],
    }

    componentDidMount() {
        this.loadAllSettings()
        this.unsubscribeVolumeMeasureStatus = onVolumeMeasureStatus(this.handleVolumeMeasureStatus)
    }

    componentWillUnmount() {
        if (this.unsubscribeVolumeMeasureStatus) this.unsubscribeVolumeMeasureStatus()
    }

    handleVolumeMeasureStatus = (data: IVolumeMeasureStatus) => {
        if (data.clientId !== this.props.clientId) return
        if (!this.state.showPouringModal) return

        this.setState({
            liveMeasuredVolume: data.volumeMl,
            liveIsPouring: data.isPouring,
            liveMeasureError: data.error ? data.errorMsg || getString('general_settings.measurement_error') : undefined,
        })
    }

    loadAllSettings = async () => {
        this.setState({ loading: true })
        try {
            const [testTube, rinse, cup, bottle, calibrationStopConfirm, ledCount, manualPourMode, homeLogo, pouringCompletionWindow, autoRinseConfig, homeScreensaver, remoteLiveLogStream, remoteTransportMode, setupCompleted, dailyAutoRestart, httpPollingInterval, loggingEnabled, sanitationPolicy, operationMode] = await Promise.all([
                getTestTubeVolume(this.props.clientId),
                getRinseDuration(this.props.clientId),
                getCupVolume(this.props.clientId),
                getBottleVolume(this.props.clientId),
                getCalibrationStopConfirm(this.props.clientId),
                getLedCount(this.props.clientId),
                getManualPourMode(this.props.clientId),
                getHomeLogo(this.props.clientId),
                getPouringCompletionWindow(this.props.clientId).catch(() => ({ completionWindowMs: 3000 })),
                getAutoRinseConfig(this.props.clientId).catch(() => ({ enabled: 0, hoursMask: 0, idleThresholdSec: 300, scheduleWaitSec: 0, durationSec: 60 })),
                getHomeScreensaver(this.props.clientId).catch(() => ({ enabled: 1, timeoutMin: 1 })),
                getRemoteLiveLogStream(this.props.clientId).catch(() => ({ enabled: 0 })),
                getRemoteTransportMode(this.props.clientId).catch(() => ({ mode: 0 })),
                getSetupCompleted(this.props.clientId).catch(() => ({ completed: 1 })),
                getDailyAutoRestart(this.props.clientId).catch(() => ({ enabled: 0 })),
                getHttpPollingInterval(this.props.clientId).catch(() => ({ intervalMs: 1000 })),
                getLoggingEnabled(this.props.clientId).catch(() => ({ enabled: 0 })),
                getSanitationPolicy(this.props.clientId).catch(() => ({ enabled: 1, intervalDays: 90 })),
                getOperationMode(this.props.clientId).catch(() => ({ mode: 'quick_pour' })),
            ])

            this.setState({
                operationMode: operationMode.mode,
                testTubeVolume: testTube.volume,
                rinseDuration: normalizeAdditionalPouringTime(rinse.duration),
                cupVolume: cup.volume,
                bottleVolume: bottle.volume,
                calibrationStopConfirmMs: calibrationStopConfirm.confirmMs,
                ledCount: ledCount.ledCount,
                manualHoldEnabled: manualPourMode.holdEnabled,
                homeLogo: homeLogo.logoKey,
                pouringCompletionWindowMs: pouringCompletionWindow?.completionWindowMs ?? 3000,
                autoRinseDurationSec: autoRinseConfig?.durationSec ?? 60,
                homeScreensaverEnabled: homeScreensaver.enabled ?? 1,
                homeScreensaverTimeoutMin: homeScreensaver.timeoutMin ?? 1,
                remoteLiveLogStreamEnabled: remoteLiveLogStream.enabled ?? 0,
                remoteTransportMode: remoteTransportMode.mode ?? 0,
                setupCompleted: setupCompleted.completed ?? 1,
                dailyAutoRestartEnabled: dailyAutoRestart.enabled ?? 0,
                httpPollingIntervalMs: httpPollingInterval.intervalMs ?? 1000,
                loggingEnabled: loggingEnabled.enabled ?? 0,
                sanitationEnabled: sanitationPolicy.enabled ?? 1,
                inactivityTimeoutSec: 60,
                loading: false,
            })
            if (this.props.onRemoteLiveLogStreamChanged) {
                this.props.onRemoteLiveLogStreamChanged((remoteLiveLogStream.enabled ?? 0) === 1)
            }

            try {
                const inactivityTimeout = await getInactivityTimeout(this.props.clientId)
                this.setState({ inactivityTimeoutSec: inactivityTimeout.inactivityTimeoutSec })
            } catch (_) {
                this.setState({ inactivityTimeoutSec: 60 })
            }

            try {
                const catalog = await getCatalogLocalList(this.props.clientId)
                const logoOptions = (catalog.data.images || []).filter(image => /_logo(\.[^.]+)?$/i.test(image.name))
                this.setState({ homeLogoOptions: logoOptions })
            } catch (_) {
                this.setState({ homeLogoOptions: [] })
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to load settings')
            this.setState({ loading: false })
        }
    }

    getSettingInfo = (type: SettingType): SettingInfo => {
        switch (type) {
            case SettingType.TestTube:
                return {
                    title: getString('general_settings.test_tube_volume'),
                    desc: getString('general_settings.test_tube_volume_desc'),
                    unit: getString('general_settings.ml'),
                    value: this.state.testTubeVolume,
                    min: 50,
                    max: 1000,
                    rangeMsg: getString('general_settings.test_tube_range'),
                    inputType: 'number',
                }
            case SettingType.Rinse:
                return {
                    title: getString('general_settings.rinse_duration'),
                    desc: getString('general_settings.rinse_duration_desc'),
                    value: formatAdditionalPouringTime(this.state.rinseDuration),
                    rangeMsg: getString('general_settings.rinse_range'),
                    inputType: 'select',
                }
            case SettingType.Cup:
                return {
                    title: getString('general_settings.cup_volume'),
                    desc: getString('general_settings.cup_volume_desc'),
                    unit: getString('general_settings.ml'),
                    value: this.state.cupVolume,
                    min: 50,
                    max: 2000,
                    rangeMsg: getString('general_settings.cup_range'),
                    inputType: 'number',
                    canMeasure: true,
                }
            case SettingType.Bottle:
                return {
                    title: getString('general_settings.bottle_volume'),
                    desc: getString('general_settings.bottle_volume_desc'),
                    unit: getString('general_settings.ml'),
                    value: this.state.bottleVolume,
                    min: 50,
                    max: 2000,
                    rangeMsg: getString('general_settings.bottle_range'),
                    inputType: 'number',
                    canMeasure: true,
                }
            case SettingType.CalibrationStopConfirm:
                return {
                    title: getString('general_settings.calibration_stop_confirm'),
                    desc: getString('general_settings.calibration_stop_confirm_desc'),
                    unit: getString('general_settings.ms'),
                    value: this.state.calibrationStopConfirmMs,
                    min: 0,
                    max: 5000,
                    rangeMsg: getString('general_settings.calibration_stop_confirm_range'),
                    inputType: 'number',
                }
            case SettingType.LedCount:
                return {
                    title: getString('general_settings.led_count'),
                    desc: getString('general_settings.led_count_desc'),
                    value: this.state.ledCount,
                    min: 1,
                    max: 300,
                    rangeMsg: getString('general_settings.led_count_range'),
                    inputType: 'number',
                }
            case SettingType.ManualHold:
                return {
                    title: getString('general_settings.manual_pour_mode'),
                    desc: getString('general_settings.manual_pour_mode_desc'),
                    value: this.state.manualHoldEnabled === 1 ? getString('general_settings.manual_mode_hold') : getString('general_settings.manual_mode_toggle'),
                    inputType: 'boolean',
                }
            case SettingType.HomeLogo:
                return {
                    title: getString('general_settings.home_logo'),
                    desc: getString('general_settings.home_logo_desc'),
                    value: this.state.homeLogo || '',
                    rangeMsg: getString('general_settings.home_logo_range'),
                    inputType: 'select',
                }
            case SettingType.PouringCompletionWindow:
                return {
                    title: getString('general_settings.pouring_completion_window'),
                    desc: getString('general_settings.pouring_completion_window_desc'),
                    unit: getString('general_settings.ms'),
                    value: this.state.pouringCompletionWindowMs,
                    min: 1000,
                    max: 10000,
                    rangeMsg: getString('general_settings.pouring_completion_window_range'),
                    inputType: 'number',
                }
            case SettingType.InactivityTimeout:
                return {
                    title: getString('general_settings.inactivity_timeout'),
                    desc: getString('general_settings.inactivity_timeout_desc'),
                    unit: getString('general_settings.seconds'),
                    value: this.state.inactivityTimeoutSec,
                    min: 5,
                    max: 3600,
                    rangeMsg: getString('general_settings.inactivity_timeout_range'),
                    inputType: 'number',
                }
            case SettingType.AutoRinseDuration:
                return {
                    title: getString('general_settings.auto_rinse_duration'),
                    desc: getString('general_settings.auto_rinse_duration_desc'),
                    unit: getString('general_settings.seconds'),
                    value: this.state.autoRinseDurationSec,
                    min: 1,
                    max: 3600,
                    rangeMsg: getString('general_settings.auto_rinse_duration_range'),
                    inputType: 'number',
                }
            case SettingType.HomeScreensaverEnabled:
                return {
                    title: getString('general_settings.home_screensaver'),
                    desc: getString('general_settings.home_screensaver_desc'),
                    value: this.state.homeScreensaverEnabled === 1 ? getString('general_settings.enabled') : getString('general_settings.disabled'),
                    inputType: 'boolean',
                }
            case SettingType.HomeScreensaverTimeout:
                return {
                    title: getString('general_settings.home_screensaver_timeout'),
                    desc: getString('general_settings.home_screensaver_timeout_desc'),
                    unit: getString('general_settings.minutes'),
                    value: this.state.homeScreensaverTimeoutMin,
                    min: 1,
                    max: 120,
                    rangeMsg: getString('general_settings.home_screensaver_timeout_range'),
                    inputType: 'number',
                }
            case SettingType.RemoteLiveLogStream:
                return {
                    title: getString('general_settings.remote_live_log_stream'),
                    desc: getString('general_settings.remote_live_log_stream_desc'),
                    value: this.state.remoteLiveLogStreamEnabled === 1 ? getString('general_settings.enabled') : getString('general_settings.disabled'),
                    inputType: 'boolean',
                }
            case SettingType.RemoteTransportMode:
                return {
                    title: getString('general_settings.remote_transport_mode'),
                    desc: getString('general_settings.remote_transport_mode_desc'),
                    value:
                        (this.state.remoteTransportMode ?? 0) === 1
                            ? getString('general_settings.remote_transport_mode_http')
                            : getString('general_settings.remote_transport_mode_tcp'),
                    inputType: 'select',
                }
            case SettingType.SetupCompleted:
                return {
                    title: getString('general_settings.setup_completed'),
                    desc: getString('general_settings.setup_completed_desc'),
                    value: this.state.setupCompleted === 1 ? getString('general_settings.enabled') : getString('general_settings.disabled'),
                    inputType: 'boolean',
                }
            case SettingType.DailyAutoRestart:
                return {
                    title: getString('general_settings.daily_auto_restart_enabled'),
                    desc: getString('general_settings.daily_auto_restart_enabled_desc'),
                    value: this.state.dailyAutoRestartEnabled === 1 ? getString('general_settings.enabled') : getString('general_settings.disabled'),
                    inputType: 'boolean',
                }
            case SettingType.HttpPollingInterval:
                return {
                    title: getString('general_settings.http_polling_interval'),
                    desc: getString('general_settings.http_polling_interval_desc'),
                    value: ((this.state.httpPollingIntervalMs ?? 1000) / 1000).toFixed(1),
                    unit: getString('general_settings.seconds'),
                    min: 0.1,
                    max: 60,
                    rangeMsg: getString('general_settings.http_polling_interval_range'),
                    inputType: 'number',
                }
            case SettingType.LoggingEnabled:
                return {
                    title: getString('general_settings.logging_enabled'),
                    desc: getString('general_settings.logging_enabled_desc'),
                    value: this.state.loggingEnabled === 1 ? getString('general_settings.enabled') : getString('general_settings.disabled'),
                    inputType: 'boolean',
                }
            case SettingType.SanitationEnabled:
                return {
                    title: getString('general_settings.sanitation_enabled'),
                    desc: getString('general_settings.sanitation_enabled_desc'),
                    value: this.state.sanitationEnabled === 1 ? getString('general_settings.enabled') : getString('general_settings.disabled'),
                    inputType: 'boolean',
                }
            case SettingType.OperationMode:
                return {
                    title: getString('general_settings.operation_mode'),
                    desc: getString('general_settings.operation_mode_desc'),
                    value: this.state.operationMode === 'self_service'
                        ? getString('general_settings.operation_mode_self_service')
                        : getString('general_settings.operation_mode_quick_pour'),
                    inputType: 'boolean',
                }
        }
    }

    openEditModal = (type: SettingType) => {
        const info = this.getSettingInfo(type)
        let value = ''
        if (type === SettingType.ManualHold) {
            value = this.state.manualHoldEnabled === 1 ? '1' : '0'
        } else if (type === SettingType.HomeScreensaverEnabled) {
            value = this.state.homeScreensaverEnabled === 1 ? '1' : '0'
        } else if (type === SettingType.RemoteLiveLogStream) {
            value = this.state.remoteLiveLogStreamEnabled === 1 ? '1' : '0'
        } else if (type === SettingType.RemoteTransportMode) {
            value = String(this.state.remoteTransportMode ?? 0)
        } else if (type === SettingType.SetupCompleted) {
            value = this.state.setupCompleted === 1 ? '1' : '0'
        } else if (type === SettingType.DailyAutoRestart) {
            value = this.state.dailyAutoRestartEnabled === 1 ? '1' : '0'
        } else if (type === SettingType.HttpPollingInterval) {
            value = (((this.state.httpPollingIntervalMs ?? 1000) / 1000).toFixed(1))
        } else if (type === SettingType.LoggingEnabled) {
            value = this.state.loggingEnabled === 1 ? '1' : '0'
        } else if (type === SettingType.SanitationEnabled) {
            value = this.state.sanitationEnabled === 1 ? '1' : '0'
        } else if (type === SettingType.OperationMode) {
            value = this.state.operationMode === 'self_service' ? '1' : '0'
        } else if (type === SettingType.Rinse) {
            value = String(normalizeAdditionalPouringTime(this.state.rinseDuration))
        } else if (type === SettingType.HomeLogo) {
            value = String(this.state.homeLogo || '')
        } else {
            value = info.value !== undefined ? String(info.value) : ''
        }

        this.setState({
            showEditModal: true,
            editingSetting: type,
            editValue: value,
        })
    }

    closeEditModal = () => {
        this.setState({
            showEditModal: false,
            editingSetting: undefined,
            editValue: '',
        })
    }

    handleSave = async () => {
        const { editingSetting, editValue } = this.state
        if (!editingSetting) return

        try {
            if (editingSetting === SettingType.HomeLogo) {
                if (editValue.length > 128) {
                    toast.error(getString('general_settings.home_logo_range'))
                    return
                }
                await setHomeLogo(this.props.clientId, editValue)
                this.setState({ homeLogo: editValue })
            } else if (editingSetting === SettingType.ManualHold) {
                const parsed = editValue === '1' ? 1 : 0
                await setManualPourMode(this.props.clientId, parsed)
                this.setState({ manualHoldEnabled: parsed })
            } else if (editingSetting === SettingType.HomeScreensaverEnabled) {
                const enabled = editValue === '1' ? 1 : 0
                await setHomeScreensaver(this.props.clientId, enabled, this.state.homeScreensaverTimeoutMin ?? 1)
                this.setState({ homeScreensaverEnabled: enabled })
            } else if (editingSetting === SettingType.RemoteLiveLogStream) {
                const enabled = editValue === '1' ? 1 : 0
                await setRemoteLiveLogStream(this.props.clientId, enabled)
                this.setState({ remoteLiveLogStreamEnabled: enabled })
                if (this.props.onRemoteLiveLogStreamChanged) {
                    this.props.onRemoteLiveLogStreamChanged(enabled === 1)
                }
            } else if (editingSetting === SettingType.RemoteTransportMode) {
                const mode = editValue === '1' ? 1 : 0
                await setRemoteTransportMode(this.props.clientId, mode)
                this.setState({ remoteTransportMode: mode })
            } else if (editingSetting === SettingType.SetupCompleted) {
                const completed = editValue === '1' ? 1 : 0
                await setSetupCompleted(this.props.clientId, completed)
                this.setState({ setupCompleted: completed })
            } else if (editingSetting === SettingType.DailyAutoRestart) {
                const enabled = editValue === '1' ? 1 : 0
                await setDailyAutoRestart(this.props.clientId, enabled)
                this.setState({ dailyAutoRestartEnabled: enabled })
            } else if (editingSetting === SettingType.HttpPollingInterval) {
                const seconds = parseFloat(editValue)
                if (isNaN(seconds) || seconds < 0.1 || seconds > 60) {
                    toast.error(getString('general_settings.http_polling_interval_range'))
                    return
                }
                const intervalMs = Math.round(seconds * 1000)
                await setHttpPollingInterval(this.props.clientId, intervalMs)
                this.setState({ httpPollingIntervalMs: intervalMs })
            } else if (editingSetting === SettingType.LoggingEnabled) {
                const enabled = editValue === '1' ? 1 : 0
                await setLoggingEnabled(this.props.clientId, enabled)
                this.setState({ loggingEnabled: enabled })
            } else if (editingSetting === SettingType.SanitationEnabled) {
                const enabled = editValue === '1' ? 1 : 0
                const policy = await getSanitationPolicy(this.props.clientId)
                await setSanitationPolicy(this.props.clientId, enabled, policy.intervalDays)
                this.setState({ sanitationEnabled: enabled })
            } else if (editingSetting === SettingType.OperationMode) {
                const mode = editValue === '1' ? 'self_service' : 'quick_pour'
                await setOperationMode(this.props.clientId, mode)
                this.setState({ operationMode: mode })
            } else if (editingSetting === SettingType.Rinse) {
                const value = parseInt(editValue, 10)
                if (!ADDITIONAL_POURING_TIME_OPTIONS.includes(value)) {
                    toast.error(getString('general_settings.rinse_range'))
                    return
                }
                await setRinseDuration(this.props.clientId, value)
                this.setState({ rinseDuration: value })
            } else {
                const value = parseInt(editValue, 10)
                if (isNaN(value)) {
                    toast.error(getString('general_settings.invalid_range'))
                    return
                }

                const info = this.getSettingInfo(editingSetting as SettingType)
                if (info.min !== undefined && value < info.min) {
                    toast.error(info.rangeMsg || getString('general_settings.invalid_range'))
                    return
                }
                if (info.max !== undefined && value > info.max) {
                    toast.error(info.rangeMsg || getString('general_settings.invalid_range'))
                    return
                }

                switch (editingSetting) {
                    case SettingType.TestTube:
                        await setTestTubeVolume(this.props.clientId, value)
                        this.setState({ testTubeVolume: value })
                        break
                    case SettingType.Cup:
                        await setCupVolume(this.props.clientId, value)
                        this.setState({ cupVolume: value })
                        break
                    case SettingType.Bottle:
                        await setBottleVolume(this.props.clientId, value)
                        this.setState({ bottleVolume: value })
                        break
                    case SettingType.CalibrationStopConfirm:
                        await setCalibrationStopConfirm(this.props.clientId, value)
                        this.setState({ calibrationStopConfirmMs: value })
                        break
                    case SettingType.LedCount:
                        await setLedCount(this.props.clientId, value)
                        this.setState({ ledCount: value })
                        break
                    case SettingType.PouringCompletionWindow:
                        await setPouringCompletionWindow(this.props.clientId, value)
                        this.setState({ pouringCompletionWindowMs: value })
                        break
                    case SettingType.InactivityTimeout:
                        await setInactivityTimeout(this.props.clientId, value)
                        this.setState({ inactivityTimeoutSec: value })
                        break
                    case SettingType.AutoRinseDuration: {
                        const config =
                            (await getAutoRinseConfig(this.props.clientId).catch(() => undefined)) ||
                            {
                                enabled: 0,
                                hoursMask: 0,
                                idleThresholdSec: 300,
                                scheduleWaitSec: 0,
                                durationSec: this.state.autoRinseDurationSec ?? 60,
                            }
                        await setAutoRinseConfig(this.props.clientId, {
                            enabled: config.enabled,
                            hoursMask: config.hoursMask,
                            idleThresholdSec: config.idleThresholdSec,
                            scheduleWaitSec: config.scheduleWaitSec,
                            durationSec: value,
                        })
                        this.setState({ autoRinseDurationSec: value })
                        break
                    }
                    case SettingType.HomeScreensaverTimeout:
                        await setHomeScreensaver(this.props.clientId, this.state.homeScreensaverEnabled === 1 ? 1 : 0, value)
                        this.setState({ homeScreensaverTimeoutMin: value })
                        break
                }
            }

            toast.success(getString('general_settings.value_updated'))
            this.closeEditModal()
        } catch (error: any) {
            toast.error(error.message || getString('general_settings.update_error'))
        }
    }

    openMeasureModal = async (type: 'cup' | 'bottle') => {
        this.setState({
            showMeasureModal: true,
            measuringSetting: type,
            loadingDrinks: true,
            drinks: [],
        })

        try {
            const response = await getDrinkList(this.props.clientId)
            const baseDrinks = (response.data.drinks || []).filter(drink => drink.flavourChannel === 0xFF)
            this.setState({ drinks: baseDrinks, loadingDrinks: false })
        } catch (error: any) {
            toast.error(error.message || 'Failed to load drinks')
            this.setState({ loadingDrinks: false, showMeasureModal: false })
        }
    }

    closeMeasureModal = () => {
        this.setState({
            showMeasureModal: false,
            measuringSetting: undefined,
            drinks: [],
            loadingDrinks: false,
        })
    }

    startMeasurement = async (drink: DrinkInfo) => {
        const { measuringSetting } = this.state
        if (!measuringSetting) return

        this.setState({
            showMeasureModal: false,
            showPouringModal: true,
            pouringDrinkName: drink.title,
            liveMeasuredVolume: 0,
            liveIsPouring: 1,
            liveMeasureError: undefined,
        })

        try {
            if (measuringSetting === 'cup') {
                await startCupVolumeMeasure(this.props.clientId, drink.id)
            } else {
                await startBottleVolumeMeasure(this.props.clientId, drink.id)
            }
        } catch (error: any) {
            toast.error(error.message || getString('general_settings.measurement_error'))
            this.setState({ showPouringModal: false })
        }
    }

    stopMeasurement = async () => {
        try {
            const result = await stopVolumeMeasure(this.props.clientId)
            this.setState({
                showPouringModal: false,
                showSaveModal: true,
                measuredVolume: Math.round(result.measuredVolume),
            })
        } catch (error: any) {
            toast.error(error.message || getString('general_settings.measurement_error'))
            this.setState({ showPouringModal: false })
        }
    }

    saveMeasuredVolume = async () => {
        const { measuringSetting, measuredVolume } = this.state
        if (!measuringSetting || measuredVolume === undefined) return

        try {
            if (measuringSetting === 'cup') {
                await setCupVolume(this.props.clientId, measuredVolume)
                this.setState({ cupVolume: measuredVolume })
            } else {
                await setBottleVolume(this.props.clientId, measuredVolume)
                this.setState({ bottleVolume: measuredVolume })
            }

            toast.success(getString('general_settings.measurement_saved'))
            this.setState({
                showSaveModal: false,
                measuringSetting: undefined,
                measuredVolume: undefined,
            })
        } catch (error: any) {
            toast.error(error.message || getString('general_settings.update_error'))
        }
    }

    retryMeasurement = () => {
        const { measuringSetting } = this.state
        this.setState({
            showSaveModal: false,
            measuredVolume: undefined,
        })
        if (measuringSetting) {
            this.openMeasureModal(measuringSetting)
        }
    }

    cancelMeasurement = () => {
        this.setState({
            showPouringModal: false,
            showSaveModal: false,
            showMeasureModal: false,
            measuringSetting: undefined,
            measuredVolume: undefined,
            pouringDrinkName: undefined,
            liveMeasuredVolume: undefined,
            liveIsPouring: undefined,
            liveMeasureError: undefined,
        })
    }

    renderSettingItem = (type: SettingType) => {
        const info = this.getSettingInfo(type)

        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">{info.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{info.desc}</p>
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{getString('general_settings.current_value')}:</span>
                            <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                {info.value !== undefined ? `${info.value}${info.unit ? ` ${info.unit}` : ''}` : '—'}
                            </span>
                        </div>
                    </div>
                </div>
                <div className={info.canMeasure ? 'flex gap-3' : ''}>
                    <button
                        onClick={() => this.openEditModal(type)}
                        className={`${info.canMeasure ? 'flex-1' : 'w-full'} px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors`}
                    >
                        {getString('general_settings.edit')}
                    </button>
                    {info.canMeasure && (
                        <button
                            onClick={() => this.openMeasureModal(type === SettingType.Cup ? 'cup' : 'bottle')}
                            className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                        >
                            {getString('general_settings.measure')}
                        </button>
                    )}
                </div>
            </div>
        )
    }

    renderEditModal = () => {
        const { showEditModal, editingSetting, editValue } = this.state
        if (!showEditModal || !editingSetting) return null

        const info = this.getSettingInfo(editingSetting as SettingType)

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{info.title}</h2>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {getString('general_settings.new_value')}
                            {info.unit ? ` (${info.unit})` : ''}
                        </label>

                        {info.inputType === 'boolean' ? (
                            <select
                                value={editValue}
                                onChange={e => this.setState({ editValue: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {editingSetting === SettingType.ManualHold ? (
                                    <>
                                        <option value="1">{getString('general_settings.manual_mode_hold')}</option>
                                        <option value="0">{getString('general_settings.manual_mode_toggle')}</option>
                                    </>
                                ) : editingSetting === SettingType.OperationMode ? (
                                    <>
                                        <option value="1">{getString('general_settings.operation_mode_self_service')}</option>
                                        <option value="0">{getString('general_settings.operation_mode_quick_pour')}</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="1">{getString('general_settings.enabled')}</option>
                                        <option value="0">{getString('general_settings.disabled')}</option>
                                    </>
                                )}
                            </select>
                        ) : info.inputType === 'select' && editingSetting === SettingType.Rinse ? (
                            <select
                                value={editValue}
                                onChange={e => this.setState({ editValue: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {ADDITIONAL_POURING_TIME_OPTIONS.map(option => (
                                    <option key={option} value={String(option)}>
                                        {formatAdditionalPouringTime(option)}
                                    </option>
                                ))}
                            </select>
                        ) : info.inputType === 'select' && editingSetting === SettingType.RemoteTransportMode ? (
                            <select
                                value={editValue}
                                onChange={e => this.setState({ editValue: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="0">{getString('general_settings.remote_transport_mode_tcp')}</option>
                                <option value="1">{getString('general_settings.remote_transport_mode_http')}</option>
                            </select>
                        ) : info.inputType === 'select' ? (
                            <select
                                value={editValue}
                                onChange={e => this.setState({ editValue: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">(None)</option>
                                {!!editValue && !this.state.homeLogoOptions.some(option => option.name === editValue) && (
                                    <option value={editValue}>{editValue}</option>
                                )}
                                {this.state.homeLogoOptions.map(option => (
                                    <option key={option.id} value={option.name}>
                                        {option.title} ({option.name})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type={info.inputType === 'number' ? 'number' : 'text'}
                                value={editValue}
                                onChange={e => this.setState({ editValue: e.target.value })}
                                min={info.min}
                                max={info.max}
                                maxLength={info.inputType === 'string' ? 128 : undefined}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                autoFocus
                            />
                        )}
                        {!!info.rangeMsg && <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{info.rangeMsg}</p>}
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={this.closeEditModal}
                            className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
                        >
                            {getString('general_settings.cancel')}
                        </button>
                        <button
                            onClick={this.handleSave}
                            className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
                        >
                            {getString('general_settings.save')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    renderMeasureModal = () => {
        const { showMeasureModal, loadingDrinks, drinks } = this.state
        if (!showMeasureModal) return null

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{getString('general_settings.measure_title')}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{getString('general_settings.select_drink')}</p>

                    {loadingDrinks ? (
                        <div className="flex justify-center py-8">
                            <Loader />
                        </div>
                    ) : drinks.length === 0 ? (
                        <div className="py-6 text-sm text-gray-600 dark:text-gray-400">
                            {getString('general_settings.no_base_drinks_for_measure')}
                        </div>
                    ) : (
                        <div className="max-h-96 overflow-y-auto mb-4">
                            {drinks.map(drink => (
                                <button
                                    key={drink.id}
                                    onClick={() => this.startMeasurement(drink)}
                                    className="w-full px-4 py-3 mb-2 bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900 text-gray-800 dark:text-white rounded-lg text-left transition-colors"
                                >
                                    <div className="font-medium">{drink.title}</div>
                                    {!!drink.description && <div className="text-sm text-gray-600 dark:text-gray-400">{drink.description}</div>}
                                </button>
                            ))}
                        </div>
                    )}

                    <button
                        onClick={this.closeMeasureModal}
                        className="w-full px-4 py-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
                    >
                        {getString('general_settings.cancel')}
                    </button>
                </div>
            </div>
        )
    }

    renderPouringModal = () => {
        const { showPouringModal, pouringDrinkName, liveMeasuredVolume, liveIsPouring, liveMeasureError } = this.state
        if (!showPouringModal) return null

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{getString('general_settings.measuring')}</h2>

                    <div className="text-center py-6">
                        <Loader />
                        <p className="text-gray-600 dark:text-gray-400 mt-4">
                            {getString('general_settings.pouring')} {pouringDrinkName}...
                        </p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400 mt-3">
                            {(liveMeasuredVolume || 0).toFixed(1)} {getString('general_settings.ml')}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {liveIsPouring === 1 ? getString('general_settings.measure_status_pouring') : getString('general_settings.measure_status_idle')}
                        </p>
                        {!!liveMeasureError && <p className="text-sm text-red-500 mt-2">{liveMeasureError}</p>}
                    </div>

                    <button
                        onClick={this.stopMeasurement}
                        className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
                    >
                        {getString('general_settings.stop_measuring')}
                    </button>
                </div>
            </div>
        )
    }

    renderSaveModal = () => {
        const { showSaveModal, measuredVolume, measuringSetting } = this.state
        if (!showSaveModal || measuredVolume === undefined) return null

        const settingName = measuringSetting === 'cup' ? getString('general_settings.cup_volume') : getString('general_settings.bottle_volume')

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{getString('general_settings.measured_volume')}</h2>

                    <div className="text-center py-6">
                        <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                            {measuredVolume} {getString('general_settings.ml')}
                        </div>
                        <p className="text-gray-600 dark:text-gray-400">{settingName}</p>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={this.cancelMeasurement}
                            className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
                        >
                            {getString('general_settings.cancel')}
                        </button>
                        <button
                            onClick={this.retryMeasurement}
                            className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium transition-colors"
                        >
                            {getString('general_settings.retry_measurement')}
                        </button>
                        <button
                            onClick={this.saveMeasuredVolume}
                            className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                        >
                            {getString('general_settings.save_volume')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    render() {
        if (this.state.loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <Loader />
                </div>
            )
        }

        return (
            <div className="p-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">{getString('general_settings.title')}</h1>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {this.renderSettingItem(SettingType.TestTube)}
                    {this.renderSettingItem(SettingType.Rinse)}
                    {this.renderSettingItem(SettingType.Cup)}
                    {this.renderSettingItem(SettingType.Bottle)}
                    {this.renderSettingItem(SettingType.CalibrationStopConfirm)}
                    {this.renderSettingItem(SettingType.LedCount)}
                    {this.renderSettingItem(SettingType.PouringCompletionWindow)}
                    {this.renderSettingItem(SettingType.InactivityTimeout)}
                    {this.renderSettingItem(SettingType.AutoRinseDuration)}
                    {this.renderSettingItem(SettingType.HomeScreensaverEnabled)}
                    {this.renderSettingItem(SettingType.HomeScreensaverTimeout)}
                    {this.renderSettingItem(SettingType.DailyAutoRestart)}
                    {this.renderSettingItem(SettingType.RemoteLiveLogStream)}
                    {this.renderSettingItem(SettingType.RemoteTransportMode)}
                    {this.renderSettingItem(SettingType.HttpPollingInterval)}
                    {this.renderSettingItem(SettingType.LoggingEnabled)}
                    {this.renderSettingItem(SettingType.SanitationEnabled)}
                    {this.renderSettingItem(SettingType.OperationMode)}
                    {this.renderSettingItem(SettingType.SetupCompleted)}
                    {this.renderSettingItem(SettingType.ManualHold)}
                    {this.renderSettingItem(SettingType.HomeLogo)}
                </div>

                {this.renderEditModal()}
                {this.renderMeasureModal()}
                {this.renderPouringModal()}
                {this.renderSaveModal()}
            </div>
        )
    }
}
